/**
 * authTransport.ts
 *
 * WHO: `AuthContext.tsx`, `CampaignService.ts`, `CharacterService.ts` and
 * `SessionService.ts` use this transport; `LegalAcceptanceService.ts` also
 * uses it for PRD002.6. `apiClient.ts` may delegate to this later;
 * it still manages its own bearer/refresh logic as of this wave.
 * WHAT: Centralizes the *transport* decision for an authenticated request —
 * which credentials to attach to a `fetch()` call — separately from the
 * *authentication* decision (which remains solely `GET /api/auth/session`'s
 * response body; see `AuthContext.checkSessionStatus`). Exports
 * `buildAuthHeaders()` for callers building their own `fetch()` options, and
 * `authorizedFetch()` for callers that just want a `fetch()`-compatible
 * wrapper.
 * WHERE: src/services/api/authTransport.ts. No React, no component state.
 * WHEN: Called on every request that should authenticate as the current
 * user: the session/profile checks in `AuthContext`, every `CampaignService`
 * call, and current `CharacterService`, `SessionService` and policy-acceptance calls.
 * WHY: mythbound.app (frontend) and the API on onrender.com are cross-site.
 * Safari and other browsers that block third-party cookies by default never
 * send the session cookie on a cross-site request, which would silently
 * sign those users out if cookie auth were the only transport. The bearer
 * header is sent ALONGSIDE `credentials: 'include'`, never instead of it,
 * and its presence or absence never by itself decides `authStatus` — only a
 * `GET /api/auth/session` response does that. This module must never decode
 * the JWT for identity; it only reads the locally recorded `token_expiry` to
 * decide whether the token is worth sending or worth refreshing first.
 */

import { getApiBaseUrl } from '../../utils/environment';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';
/** Treat a token as expired slightly before its real expiry to avoid a race
 * where it expires mid-flight between the check and the server receiving it. */
const EXPIRY_SAFETY_MARGIN_MS = 5000;

interface StoredTokens {
  accessToken: string;
  expiry: number;
}

function readStoredTokens(): StoredTokens | null {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const expiryRaw = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!accessToken || !expiryRaw) return null;
  const expiry = parseInt(expiryRaw, 10);
  if (Number.isNaN(expiry)) return null;
  return { accessToken, expiry };
}

function isExpired(expiry: number): boolean {
  return expiry <= Date.now() + EXPIRY_SAFETY_MARGIN_MS;
}

/** Clears every locally stored token. Never touches the server session cookie. */
export function clearStoredTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
}

function persistTokens(data: { access_token: string; refresh_token?: string; expires_in?: number }): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
  if (data.refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
  const expiryTime = Date.now() + Number(data.expires_in ?? 3600) * 1000;
  localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiryTime));
}

/**
 * Attempt exactly one refresh using the stored refresh token.
 *
 * @returns the new access token on success; `null` on any failure. On
 * failure this also clears every stored token — a failed refresh means the
 * held tokens are no longer usable, so keeping them around would just cause
 * every subsequent request to repeat the same failed refresh.
 */
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    clearStoredTokens();
    return null;
  }
  try {
    const response = await fetch(`${getApiBaseUrl()}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!response.ok) {
      clearStoredTokens();
      return null;
    }
    const data = await response.json();
    if (!data?.access_token) {
      clearStoredTokens();
      return null;
    }
    persistTokens(data);
    return data.access_token;
  } catch {
    clearStoredTokens();
    return null;
  }
}

/**
 * Build the `Authorization` header (if any) for the next request.
 *
 * - No stored token → `{}` (cookie-only; the caller must still send
 *   `credentials: 'include'` itself, or use `authorizedFetch`).
 * - A non-expired stored token → `{ Authorization: 'Bearer <token>' }`.
 * - An expired stored token → attempts one refresh; returns the refreshed
 *   bearer header on success, or `{}` (tokens cleared) on failure.
 */
/**
 * Local browser token data supplies an optional transport credential only;
 * callers must still treat the server session response as identity authority.
 */
export async function buildAuthHeaders(): Promise<Record<string, string>> {
  const stored = readStoredTokens();
  if (!stored) return {};
  if (!isExpired(stored.expiry)) {
    return { Authorization: `Bearer ${stored.accessToken}` };
  }
  const refreshed = await refreshAccessToken();
  return refreshed ? { Authorization: `Bearer ${refreshed}` } : {};
}

/**
 * Best-effort read of a JSON error body's `code` field, without throwing.
 *
 * Backend contract (binding, aligned with the API): auth error bodies are
 * shaped as `{"detail": {"message": "<text>", "code": "<code>"}}` — codes are
 * `token_expired`, `credential_conflict`, `invalid_credentials`, `revoked`,
 * `not_authenticated` (401), `origin_rejected` (403), `auth_unavailable`
 * (503). `revoked` means the presented token was issued before a logout;
 * treated the same as `invalid_credentials` here (no refresh — see below).
 * Reads `body.detail.code` first; falls back to a flat `body.code`
 * tolerantly, in case a non-conforming response shapes it differently.
 */
async function readErrorCode(response: Response): Promise<string | null> {
  try {
    const body = await response.clone().json();
    const nestedCode = body?.detail?.code;
    if (typeof nestedCode === 'string') return nestedCode;
    return typeof body?.code === 'string' ? body.code : null;
  } catch {
    return null;
  }
}

/**
 * 401 codes that mean the presented credential is definitively bad (not
 * merely stale) — `token_expired` is handled separately (refresh-and-retry).
 * Encountering any of these clears stored tokens immediately, rather than
 * waiting for the caller's own next session check: a token the backend has
 * just rejected as revoked/invalid/conflicting will only be rejected again.
 */
const REJECTED_CREDENTIAL_CODES = new Set(['credential_conflict', 'invalid_credentials', 'revoked', 'not_authenticated']);

/**
 * `fetch()` wrapper that always sends `credentials: 'include'` plus a bearer
 * header when one is available (see `buildAuthHeaders`), and implements the
 * backend's single refresh-and-retry contract:
 *
 * A 401 response body with `code: 'token_expired'` means the presented
 * bearer (if any) or the session itself needs a fresh token — this function
 * refreshes exactly once and retries the request exactly once with the new
 * token.
 *
 * A 401 with `code: 'revoked'`, `'invalid_credentials'`, `'credential_conflict'`,
 * or `'not_authenticated'` means the credential is definitively rejected, not
 * merely expired: no refresh is attempted, and any stored tokens are cleared
 * immediately (see `REJECTED_CREDENTIAL_CODES`) — `revoked` specifically means
 * the token was issued before a logout, so it can never become valid again by
 * refreshing. Any 401 with no recognized code, or a retry that still 401s, is
 * returned to the caller as-is with no token clearing (ambiguous — this
 * function doesn't guess). Callers treat any returned 401 as "unauthenticated";
 * this function never loops.
 */
export async function authorizedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const authHeaders = await buildAuthHeaders();
  const response = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: { ...(init.headers || {}), ...authHeaders },
  });

  if (response.status === 401) {
    const code = await readErrorCode(response);
    if (code === 'token_expired') {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        return fetch(url, {
          ...init,
          credentials: 'include',
          headers: { ...(init.headers || {}), Authorization: `Bearer ${refreshed}` },
        });
      }
    } else if (code && REJECTED_CREDENTIAL_CODES.has(code)) {
      clearStoredTokens();
    }
  }

  return response;
}
